// The public landing page (PRD §3.1). Logo centred toward the top, a fun on-brand
// login form. This is the ONLY thing a logged-out visitor ever sees.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../lib/api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Logo } from "../components/Logo";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const shake = useAnimationControls();

  const [handle, setHandle] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(handle, password);
      // Messy (admin) lives in the admin area; friends land on the feed.
      navigate(u.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went sideways. Try again.");
      setSubmitting(false);
      if (!reduce) void shake.start({ x: [0, -9, 8, -6, 5, 0], transition: { duration: 0.42 } });
    }
  }

  const rise = (delay: number) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay } }
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
        };

  return (
    <main className="paper-grain relative flex min-h-dvh flex-col items-center justify-center overflow-x-clip px-6 py-10">
      {/* Warm sunlight bleeding in behind the logo — the one citrus accent. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-20 h-72 w-72 -translate-x-1/2 rounded-full bg-citrus/30 blur-3xl"
      />

      <div className="relative z-10 flex w-full max-w-sm flex-col items-center">
        {/* Logo hero — framed like a printed zine card pinned to the page. */}
        <motion.div
          {...rise(0)}
          className="rotate-[-1.5deg] rounded-2xl border-2 border-ink bg-white p-2 shadow-[6px_6px_0_0_var(--color-ink)]"
        >
          <Logo className="block w-full max-w-[290px] rounded-lg" />
        </motion.div>

        <motion.p {...rise(0.12)} className="stamp mt-6 text-center text-[0.7rem] text-ink-muted">
          Private farm dispatches · invite only
        </motion.p>

        {/* Login form — outer div handles the entrance reveal, inner form the
            error shake (so the shake control doesn't clobber the fade-in). */}
        <motion.div {...rise(0.2)} className="mt-6 w-full">
          <motion.form onSubmit={onSubmit} className="w-full" animate={shake} noValidate>
            <div className="flex flex-col gap-4 rounded-2xl border-2 border-ink/15 bg-paper/70 p-5 backdrop-blur-sm">
            <Field
              label="Your username"
              name="username"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="e.g. LekkerBakkie"
              required
            />

            <div>
              <Field
                label="Your password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="e.g. koeksister"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="mt-1.5 text-xs font-semibold text-ink-muted underline decoration-dotted underline-offset-2 hover:text-ink"
              >
                {showPassword ? "Hide password" : "Show password"}
              </button>
            </div>

            {error ? (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                role="alert"
                className="rounded-lg border-2 border-love/30 bg-love/5 px-3 py-2 text-sm font-medium text-love"
              >
                {error}
              </motion.p>
            ) : null}

            <Button type="submit" loading={submitting} className="mt-1 w-full">
              {submitting ? "Opening the gate…" : "Kom binne 🍊"}
            </Button>
            </div>
          </motion.form>
        </motion.div>

        <motion.p {...rise(0.3)} className="mt-6 max-w-xs text-center text-xs leading-relaxed text-ink-muted">
          You'll stay logged in on this phone — no need to sign in again.
        </motion.p>
      </div>
    </main>
  );
}
